import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import RootStoreContext from '~/contexts/RootStoreContext';
import {
  actions as commonActions,
  selectors as commonSelectors
} from '~/domains/common';
import {
  actions as modalActions,
  selectors as modalSelectors
} from '~/domains/modal';
import { actions as agentScannersActions } from '~/domains/agentScanners';
import withLayoutContext from '~/components/LayoutContextProvider';
import { setModalType, registerModal } from '~/components/Modals';
import { getPath } from '~/utils/routes';
import { getActiveOrganizationId } from '~/utils/organization';
import DeleteConfirmModal from './DeleteConfirmModal';

const mapStateToProps = (state) => {
  const profile = commonSelectors.profile(state);
  const modalProps = modalSelectors.modalProps(state);

  return {
    profile,
    organizationId: getActiveOrganizationId(profile),
    scanConfig: modalProps?.scanConfig,
    navigateToList: modalProps?.navigateToList
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  onCancel: () => dispatch(modalActions.closeModal()),
  /**
   * Handles scanner deletion confirmation.
   * @param {Object} scanner - Scanner object from modalProps.scanConfig (passed by DeleteConfirmModal component)
   * @param {string} [scanner.id] - Scanner ID for deletion
   * @param {string} [scanner.name] - Scanner name (validated with optional chaining + fallback)
   * @param {string} organizationId - The organization ID
   * @param {boolean} navigateToList - Whether to navigate back to list after deletion
   * @param {string} deleteType - Type of deletion operation
   */
  onConfirm: (scanner, organizationId, navigateToList, deleteType) => {
    dispatch(
      agentScannersActions.deleteAgentScanner(
        scanner.id,
        organizationId,
        deleteType
      )
    )
      .then(() => {
        dispatch(modalActions.closeModal());

        // isHTML: false - no HTML needed, safe by default
        const deleteAlert = {
          type: 'success',
          message: `Scanner '${scanner?.name || 'Unknown'}' is being deleted.`,
          isHTML: false
        };

        dispatch(commonActions.setAlert(deleteAlert));

        // Navigate to the scanners list if navigateToList is true
        // (used when deleting from the scanner detail view)
        if (navigateToList) {
          if (!ownProps.history) {
            // eslint-disable-next-line no-console
            console.warn(
              'Navigation requested but history object is not available'
            );

            return;
          }

          const scannersListPath = getPath('agentScanners');

          if (!scannersListPath) {
            // eslint-disable-next-line no-console
            console.error('Failed to get scanners list path');

            return;
          }

          ownProps.history.push(scannersListPath);
        }
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to delete scanner:', error);
        dispatch(modalActions.closeModal());
      });
  }
});

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...ownProps,
  ...stateProps,
  onCancel: dispatchProps.onCancel,
  onConfirm: (deleteType) =>
    dispatchProps.onConfirm(
      stateProps.scanConfig,
      stateProps.organizationId,
      stateProps.navigateToList,
      deleteType
    )
});

export default compose(
  registerModal,
  setModalType('DeleteConfirmModal'),
  withLayoutContext(),
  withRouter,
  connect(mapStateToProps, mapDispatchToProps, mergeProps, {
    context: RootStoreContext
  })
)(DeleteConfirmModal);
